import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Import dashboard components
import DashboardLayout from './dashboard/DashboardLayout';
import DashboardStats from './dashboard/DashboardStats';
import UnifiedScheduler from './dashboard/redesign/UnifiedScheduler';
import Patient360Timeline from './dashboard/redesign/Patient360Timeline';
import TeamManagement from './dashboard/TeamManagement';
import SecureIntakeDrawer from './dashboard/redesign/SecureIntakeDrawer';
import ContactSubmissions from './dashboard/ContactSubmissions';

const Dashboard = () => {
  // State for refresh message
  const [refreshMsg, setRefreshMsg] = useState('');
  const [submissions, setSubmissions] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [requests, setRequests] = useState([]);
  const [users, setUsers] = useState([]);
    // Get currentUser from localStorage or sessionStorage
  let userStr = localStorage.getItem('currentUser') || sessionStorage.getItem('currentUser');
  let role = 'viewer';
  if (userStr) {
    try {
      const user = JSON.parse(userStr);
      if (user.role) role = user.role;
    } catch (e) {}
  }
  const navigate = useNavigate();

  // Function to reload dashboard data from server API
  const reloadDashboardData = async () => {
    setRefreshMsg('Loading data...');

    try {
      // Determine API URL based on environment
      const baseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? `http://${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}`
        : window.location.hostname === 'healthcare.fliphatmedia.com'
        ? 'https://healthcare.fliphatmedia.com'
        : '';

      // Fetch appointments
      const appointmentsUrl = `${baseUrl}/api/get_appointments.php`;
      console.log('Fetching appointments from:', appointmentsUrl);
      const appointmentsResponse = await fetch(appointmentsUrl, {
        credentials: 'include'
      });
      if (appointmentsResponse.ok) {
        const appointmentsData = await appointmentsResponse.json();
        if (appointmentsData.success && Array.isArray(appointmentsData.appointments)) {
          setAppointments(appointmentsData.appointments);
          // Also update localStorage as a backup
          localStorage.setItem('appointments', JSON.stringify(appointmentsData.appointments));
        } else {
          console.error('Invalid appointments data format:', appointmentsData);
          // Fallback to localStorage if API fails
          const localAppts = JSON.parse(localStorage.getItem('appointments') || '[]');
          setAppointments(localAppts);
        }
      } else {
        console.error('Failed to fetch appointments:', appointmentsResponse.status);
        // Fallback to localStorage if API fails
        const localAppts = JSON.parse(localStorage.getItem('appointments') || '[]');
        setAppointments(localAppts);
      }

      // Fetch contact submissions
      const contactsUrl = `${baseUrl}/api/get_contacts.php`;
      console.log('Fetching contacts from:', contactsUrl);
      const contactsResponse = await fetch(contactsUrl, {
        credentials: 'include'
      });
      if (contactsResponse.ok) {
        const contactsData = await contactsResponse.json();
        if (contactsData.success && Array.isArray(contactsData.contacts)) {
          setSubmissions(contactsData.contacts);
          // Also update localStorage as a backup
          localStorage.setItem('contactSubmissions', JSON.stringify(contactsData.contacts));
        } else {
          console.error('Invalid contacts data format:', contactsData);
          // Fallback to localStorage if API fails
          const localContacts = JSON.parse(localStorage.getItem('contactSubmissions') || '[]');
          setSubmissions(localContacts);
        }
      } else {
        console.error('Failed to fetch contacts:', contactsResponse.status);
        // Fallback to localStorage if API fails
        const localContacts = JSON.parse(localStorage.getItem('contactSubmissions') || '[]');
        setSubmissions(localContacts);
      }

      // Fetch users (for registration requests and team management)
      const usersUrl = `${baseUrl}/api/get_users.php`;
      console.log('Fetching users from:', usersUrl);
      const usersResponse = await fetch(usersUrl, {
        credentials: 'include'
      });
      if (usersResponse.ok) {
        const usersData = await usersResponse.json();
        if (usersData.success && Array.isArray(usersData.users)) {
          setUsers(usersData.users);
          // Filter pending users as registration requests
          const pendingUsers = usersData.users.filter(u => u.status === 'pending');
          setRequests(pendingUsers);
          // Also update localStorage as a backup
          localStorage.setItem('users', JSON.stringify(usersData.users));
          localStorage.setItem('registrationRequests', JSON.stringify(pendingUsers));
        } else {
          console.error('Invalid users data format:', usersData);
          // Fallback to localStorage if API fails
          const localUsers = JSON.parse(localStorage.getItem('users') || '[]');
          const localReqs = JSON.parse(localStorage.getItem('registrationRequests') || '[]');
          setUsers(localUsers);
          setRequests(localReqs);
        }
      } else {
        console.error('Failed to fetch users:', usersResponse.status);
        // Fallback to localStorage if API fails
        const localUsers = JSON.parse(localStorage.getItem('users') || '[]');
        const localReqs = JSON.parse(localStorage.getItem('registrationRequests') || '[]');
        setUsers(localUsers);
        setRequests(localReqs);
      }
      
      setRefreshMsg('Data refreshed!');
      setTimeout(() => setRefreshMsg(''), 1500);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setRefreshMsg('Error loading data!');
      setTimeout(() => setRefreshMsg(''), 3000);
      
      // Fallback to localStorage if API fails
      const localData = JSON.parse(localStorage.getItem('contactSubmissions') || '[]');
      setSubmissions(localData);
      const localAppts = JSON.parse(localStorage.getItem('appointments') || '[]');
      setAppointments(localAppts);
      const localReqs = JSON.parse(localStorage.getItem('registrationRequests') || '[]');
      setRequests(localReqs);
      const localUsers = JSON.parse(localStorage.getItem('users') || '[]');
      setUsers(localUsers);
    }
  };

  useEffect(() => {
    reloadDashboardData();
    // eslint-disable-next-line
  }, []);

  // Appointment handlers
  const handleDeleteAppointment = async (id) => {
    try {
      const baseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? `http://${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}`
        : window.location.hostname === 'healthcare.fliphatmedia.com'
        ? 'https://healthcare.fliphatmedia.com'
        : '';
      
      const response = await fetch(`${baseUrl}/api/delete_appointment.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setAppointments(prev => prev.filter(a => a.id !== id));
        // Keep backup in localStorage
        const updated = appointments.filter(a => a.id !== id);
        localStorage.setItem('appointments', JSON.stringify(updated));
      } else {
        console.error('Failed to delete appointment:', data.error);
        alert('Failed to delete appointment: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting appointment:', error);
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      const baseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? `http://${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}`
        : window.location.hostname === 'healthcare.fliphatmedia.com'
        ? 'https://healthcare.fliphatmedia.com'
        : '';
      
      const response = await fetch(`${baseUrl}/api/update_appointment.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, status }),
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setAppointments(prev => prev.map(a => a.id === id ? { ...a, status } : a));
        // Keep backup in localStorage
        const updated = appointments.map(a => a.id === id ? { ...a, status } : a);
        localStorage.setItem('appointments', JSON.stringify(updated));
      } else {
        console.error('Failed to update status:', data.error);
        alert('Failed to update status: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  // User management handlers
  const handleLogout = () => {
    localStorage.removeItem('currentUser');
    sessionStorage.removeItem('currentUser');
    localStorage.removeItem('rememberedEmail');
    navigate('/login');
  };

  // Admin: Approve registration request
  const handleApprove = async (id) => {
    const approvedUser = requests.find(r => r.id === id);
    if (!approvedUser) return;
    try {
      const baseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? `http://${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}`
        : window.location.hostname === 'healthcare.fliphatmedia.com'
        ? 'https://healthcare.fliphatmedia.com'
        : '';
      
      const response = await fetch(`${baseUrl}/api/manage_users.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'update',
          user: {
            email: approvedUser.email,
            status: 'approved'
          }
        }),
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setRequests(prev => prev.filter(r => r.id !== id));
        setUsers(prev => prev.map(u => u.id === id ? { ...u, status: 'approved' } : u));
        
        // Update backups
        const reqs = requests.filter(r => r.id !== id);
        localStorage.setItem('registrationRequests', JSON.stringify(reqs));
        const updatedUsers = users.map(u => u.id === id ? { ...u, status: 'approved' } : u);
        localStorage.setItem('users', JSON.stringify(updatedUsers));
      } else {
        console.error('Failed to approve request:', data.error);
        alert('Failed to approve user: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error approving user:', error);
    }
  };

  // Admin: Reject registration request
  const handleReject = async (id) => {
    const rejectedUser = requests.find(r => r.id === id);
    if (!rejectedUser) return;
    try {
      const baseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? `http://${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}`
        : window.location.hostname === 'healthcare.fliphatmedia.com'
        ? 'https://healthcare.fliphatmedia.com'
        : '';
      
      const response = await fetch(`${baseUrl}/api/manage_users.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'delete',
          email: rejectedUser.email
        }),
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setRequests(prev => prev.filter(r => r.id !== id));
        setUsers(prev => prev.filter(u => u.id !== id));
        
        // Update backups
        const reqs = requests.filter(r => r.id !== id);
        localStorage.setItem('registrationRequests', JSON.stringify(reqs));
        const updatedUsers = users.filter(u => u.id !== id);
        localStorage.setItem('users', JSON.stringify(updatedUsers));
      } else {
        console.error('Failed to reject request:', data.error);
        alert('Failed to reject user: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error rejecting user:', error);
    }
  };

  // Admin: Change user role
  const handleChangeRole = async (email, newRole) => {
    try {
      const baseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? `http://${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}`
        : window.location.hostname === 'healthcare.fliphatmedia.com'
        ? 'https://healthcare.fliphatmedia.com'
        : '';
      
      const response = await fetch(`${baseUrl}/api/manage_users.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'update',
          user: {
            email,
            role: newRole
          }
        }),
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setUsers(prev => prev.map(u => u.email === email ? { ...u, role: newRole } : u));
        
        // Update backup
        const updated = users.map(u => u.email === email ? { ...u, role: newRole } : u);
        localStorage.setItem('users', JSON.stringify(updated));
      } else {
        console.error('Failed to change role:', data.error);
        alert('Failed to update role: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error changing role:', error);
    }
  };

  // Admin: Delete user
  const handleDeleteUser = async (email) => {
    try {
      const baseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? `http://${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}`
        : window.location.hostname === 'healthcare.fliphatmedia.com'
        ? 'https://healthcare.fliphatmedia.com'
        : '';
      
      const response = await fetch(`${baseUrl}/api/manage_users.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'delete',
          email
        }),
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setUsers(prev => prev.filter(u => u.email !== email));
        setRequests(prev => prev.filter(r => r.email !== email));
        
        // Update backups
        const updated = users.filter(u => u.email !== email);
        localStorage.setItem('users', JSON.stringify(updated));
        const reqs = requests.filter(r => r.email !== email);
        localStorage.setItem('registrationRequests', JSON.stringify(reqs));
      } else {
        console.error('Failed to delete user:', data.error);
        alert('Failed to delete user: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  return (
    <DashboardLayout 
      onLogout={handleLogout} 
      role={role}
      submissions={submissions}
      requests={requests}
    >
      {/* Refresh Button */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={reloadDashboardData}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded font-semibold shadow"
        >
          Refresh Data
        </button>
        {refreshMsg && (
          <span className="ml-4 text-green-600 font-medium animate-pulse">{refreshMsg}</span>
        )}
      </div>
      {/* Dashboard Statistics */}
      <div id="stats">
        <DashboardStats 
          submissions={submissions} 
          appointments={appointments} 
          users={users} 
        />
      </div>
      
      {/* Appointment Management */}
      <div id="appointments">
        <UnifiedScheduler 
          appointments={appointments} 
          onStatusChange={handleStatusChange} 
          onDelete={handleDeleteAppointment} 
        />
      </div>
      
      {/* Admin Customer Portal Control */}
      {role === 'admin' && (
        <div id="customer-portal">
          <Patient360Timeline 
            users={users} 
            appointments={appointments} 
            submissions={submissions} 
          />
        </div>
      )}
      
      {/* Contact Submissions */}
      <div id="contacts">
        <ContactSubmissions submissions={submissions} />
      </div>
      
      {/* Team Management (Admin Only) */}
      {role === 'admin' && (
        <div id="team">
          <TeamManagement 
            users={users} 
            onChangeRole={handleChangeRole} 
            onDeleteUser={handleDeleteUser} 
          />
        </div>
      )}
      
      {/* Registration Requests (Admin Only) */}
      {role === 'admin' && (
        <div id="requests">
          <SecureIntakeDrawer 
            requests={requests} 
            onApprove={handleApprove} 
            onReject={handleReject} 
          />
        </div>
      )}
    </DashboardLayout>
  );
};

export default Dashboard;